import {
  GetProps,
  interaction,
  SizableText,
  Stack,
  styled,
  YStack,
} from '@mythical/ui'
import RichText from 'app/components/primitive/RichText'
import { Container, ContainerProps } from 'app/components/structure/Container'
import React from 'react'
import { SolitoImage } from 'solito/image'
import { ButtonLink } from '../../primitive/Button'
import { SmartLink } from 'app/components/primitive/SmartLink'

const AdWrapper = styled(YStack, {
  ai: 'center',
  gap: '$3.5',
  w: '100%',
  $gtSm: {
    fd: 'row',
    gap: '$6',
  },
  variants: {
    reverse: {
      true: {
        $gtSm: {
          fd: 'row',
        },
      },
      false: {
        $gtSm: {
          fd: 'row-reverse',
        },
      },
    },
  } as const,
})

type AdWrapperProps = GetProps<typeof AdWrapper>

const AdImageWrapper = styled(Stack, {
  aspectRatio: 16 / 9,
  br: '$2',
  overflow: 'hidden',
  w: '100%',
  $gtSm: {
    aspectRatio: 9 / 6,
    flex: 1,
  },
})

const AdContent = styled(YStack, {
  ai: 'flex-start',
  gap: '$6',
  w: '100%',
  $gtSm: {
    flex: 1,
  },
})

const AdTextContent = styled(YStack, {
  gap: '$3.5',
})

export interface AdStandardProps extends ContainerProps {
  ctaLabel?: string
  description?: string
  href?: string
  image: string
  reverse?: boolean
  richTextDescription?: React.ReactNode
  title: string
}

export const AdStandard = ({
  ctaLabel,
  description,
  href,
  image,
  reverse = false,
  richTextDescription,
  title,
  ...props
}: AdStandardProps & AdWrapperProps) => {
  return (
    <Container paddedX variant="none" {...props}>
      <AdWrapper reverse={reverse}>
        <SmartLink
          href={href}
          Component={AdImageWrapper}
          tabIndex={-1}
          {...(href && { ...interaction.opacity })}
          {...(href?.startsWith('http') && { target: '_blank' })}
        >
          <SolitoImage
            alt=""
            contentFit="cover"
            fill
            sizes="(min-width: 800px) 50vw, 100vw"
            src={image}
          />
        </SmartLink>

        <AdContent>
          <AdTextContent>
            <SizableText ff="$display" tt="uppercase" $gtSm={{ size: '$l' }}>
              {title}
            </SizableText>
            {richTextDescription && (
              <RichText
                content={richTextDescription}
                textProps={{
                  size: '$s',
                  fontWeight: '500',
                  $gtSm: {
                    size: '$m',
                    fontWeight: '500',
                  },
                }}
              />
            )}
          </AdTextContent>
          {href && ctaLabel && (
            <ButtonLink
              alignSelf="flex-start"
              href={href}
              size="$4.5"
              textProps={{
                ff: '$title',
                fontWeight: '500',
              }}
              {...(href?.startsWith('http') ? { target: '_blank' } : {})}
            >
              {ctaLabel}
            </ButtonLink>
          )}
        </AdContent>
      </AdWrapper>
    </Container>
  )
}
